import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DynamicIcon from "../components/DynamicIcon";
import { getBilledJobsList, getBilledJobDetail, resetSelectedBilledJob, selectBilledJobsList, selectBilledJobsLoading, selectFilterOptions, updateFilterOptions, resetFilters, selectTotalResults } from "../redux/billedJobs/billedJobsSlice";
import { selectAccountData } from "../redux/main/accountSlice";
import { closeModal, selectRightModalId, selectRightModalIsOpen, selectRightModalIsRefreshNow, selectRightModalmode, setModal } from "../redux/rightModalSlice";
import LoadingSection from "../components/LoadingSection";
import DynamicTitle from "../components/DynamicTitle";
import BilledJobCard from "../components/billedJobsPage/BilledJobCard";
import RightModal from "../components/templates/RightModal";
import ViewBilledJob from "../components/billedJobsPage/ViewBilledJob";

export default function BilledJobsList() {
    const dispatch = useDispatch();
    const billedJobsList = useSelector(selectBilledJobsList);
    const billedJobsLoading = useSelector(selectBilledJobsLoading);
    const accountData = useSelector(selectAccountData);
    const filterOptions = useSelector(selectFilterOptions);
    const totalResults = useSelector(selectTotalResults);
    const token = localStorage.getItem('installamelo-auth-token');
    
    // RightModal gestita tramite Redux
    const rightModalIsOpen = useSelector(selectRightModalIsOpen);
    const rightModalMode = useSelector(selectRightModalmode);
    const rightModalJobId = useSelector(selectRightModalId);
    
    // Stato per i filtri
    const [searchTerm, setSearchTerm] = useState('');
    
    // Carica le commesse fatturate quando il componente viene montato
    useEffect(() => {
        if (accountData?._id) {
            dispatch(getBilledJobsList({
                accountId: accountData._id,
                token,
                filterOptions
            }));
        }
    }, [dispatch, accountData, filterOptions]);
    
    // Avvio il refresh dei dati quando isRefreshNow della RightModal si attiva
    const rightModalIsRefreshNow = useSelector(selectRightModalIsRefreshNow);
    useEffect(() => {
        if (rightModalIsRefreshNow && accountData?._id) {
            dispatch(getBilledJobsList({
                accountId: accountData._id,
                token,
                filterOptions
            }));
        }
    }, [rightModalIsRefreshNow, dispatch, accountData, filterOptions]);
    
    // Carica i dettagli della commessa quando il modale viene aperto per visualizzazione
    useEffect(() => {
        if (rightModalIsOpen && rightModalJobId && rightModalMode === 'view') {
            dispatch(getBilledJobDetail({
                jobId: rightModalJobId,
                token
            }));
        }
    }, [rightModalIsOpen, rightModalJobId, rightModalMode, dispatch, token]);
    
    // Reset della commessa selezionata quando si chiude il modale
    useEffect(() => {
        if (!rightModalIsOpen) {
            dispatch(resetSelectedBilledJob());
        }
    }, [rightModalIsOpen, dispatch]);
    
    // Gestisce la ricerca
    const handleSearch = () => {
        const newFilterOptions = {
            ...filterOptions,
            jobCode: searchTerm,
            start: 0, // Ritorna alla prima pagina
            draw: filterOptions.draw + 1
        };
        dispatch(updateFilterOptions(newFilterOptions));
    };
    
    // Gestisce il reset dei filtri
    const handleResetFilters = () => {
        setSearchTerm('');
        dispatch(resetFilters());
    };
    
    // Gestisce la paginazione
    const handlePageChange = (newPage) => {
        const newStart = newPage * filterOptions.length;
        if (newStart >= 0 && newStart < totalResults) {
            dispatch(updateFilterOptions({
                start: newStart,
                draw: filterOptions.draw + 1
            }));
        }
    };
    
    // Calcola la pagina corrente
    const currentPage = Math.floor(filterOptions.start / filterOptions.length);
    const totalPages = Math.ceil(totalResults / filterOptions.length);
    
    return (
        <div className="page">
            {billedJobsLoading && <LoadingSection isFixed />}
            <DynamicTitle title="Commesse Fatturate" />
            
            <h1>Commesse Fatturate</h1>
            
            {/* Filtri */}
            <div className="bg-white p-4 rounded-lg shadow mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <label htmlFor="searchTerm" className="block text-sm font-medium text-gray-700 mb-1">
                            Ricerca per Codice
                        </label>
                        <input
                            type="text"
                            id="searchTerm"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Inserisci il codice commessa"
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        />
                    </div>
                    <div className="flex items-end space-x-2">
                        <button
                            className="primary"
                            onClick={handleSearch}
                        >
                            <DynamicIcon iconClass="fas fa-search" />
                            Cerca
                        </button>
                        <button
                            className="secondary"
                            onClick={handleResetFilters}
                        >
                            <DynamicIcon iconClass="fas fa-undo" />
                            Reset
                        </button>
                    </div>
                </div>
            </div>
            
            {/* Mostra risultati totali e paginazione */}
            <div className="flex justify-between items-center mb-4">
                <p className="text-gray-600">
                    {totalResults > 0 
                        ? `Mostrati ${filterOptions.start + 1}-${Math.min(filterOptions.start + filterOptions.length, totalResults)} di ${totalResults} risultati` 
                        : 'Nessun risultato trovato'}
                </p>
                
                {totalPages > 1 && (
                    <div className="flex space-x-1">
                        <button
                            className="px-3 py-1 border rounded-md disabled:opacity-50"
                            disabled={currentPage === 0}
                            onClick={() => handlePageChange(currentPage - 1)}
                        >
                            <DynamicIcon iconClass="fas fa-chevron-left" />
                        </button>
                        
                        {[...Array(totalPages)].map((_, i) => (
                            Math.abs(i - currentPage) < 3 || i === 0 || i === totalPages - 1 ? (
                                <button
                                    key={i}
                                    className={`px-3 py-1 border rounded-md ${i === currentPage ? 'bg-blue-500 text-white' : ''}`}
                                    onClick={() => handlePageChange(i)}
                                >
                                    {i + 1}
                                </button>
                            ) : (
                                Math.abs(i - currentPage) === 3 && (
                                    <span key={i} className="px-2 py-1">...</span>
                                )
                            )
                        ))}
                        
                        <button
                            className="px-3 py-1 border rounded-md disabled:opacity-50"
                            disabled={currentPage >= totalPages - 1}
                            onClick={() => handlePageChange(currentPage + 1)}
                        >
                            <DynamicIcon iconClass="fas fa-chevron-right" />
                        </button>
                    </div>
                )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {billedJobsList && billedJobsList.length > 0 ? (
                    billedJobsList.map((job, index) => (
                        <BilledJobCard
                            key={job._id}
                            job={job}
                            index={index}
                        />
                    ))
                ) : !billedJobsLoading ? (
                    <div className="col-span-full text-center text-gray-500 py-8">
                        Nessuna commessa fatturata trovata. Prova a modificare i filtri di ricerca.
                    </div>
                ) : null}
            </div>
            
            {/* Paginazione inferiore */}
            {totalPages > 1 && (
                <div className="flex justify-center mt-6">
                    <div className="flex space-x-1">
                        <button
                            className="px-3 py-1 border rounded-md disabled:opacity-50"
                            disabled={currentPage === 0}
                            onClick={() => handlePageChange(currentPage - 1)}
                        >
                            <DynamicIcon iconClass="fas fa-chevron-left" />
                        </button>
                        
                        {[...Array(totalPages)].map((_, i) => (
                            Math.abs(i - currentPage) < 3 || i === 0 || i === totalPages - 1 ? (
                                <button
                                    key={i}
                                    className={`px-3 py-1 border rounded-md ${i === currentPage ? 'bg-blue-500 text-white' : ''}`}
                                    onClick={() => handlePageChange(i)}
                                >
                                    {i + 1}
                                </button>
                            ) : (
                                Math.abs(i - currentPage) === 3 && (
                                    <span key={i} className="px-2 py-1">...</span>
                                )
                            )
                        ))}
                        
                        <button
                            className="px-3 py-1 border rounded-md disabled:opacity-50"
                            disabled={currentPage >= totalPages - 1}
                            onClick={() => handlePageChange(currentPage + 1)}
                        >
                            <DynamicIcon iconClass="fas fa-chevron-right" />
                        </button>
                    </div>
                </div>
            )}
            
            {/* RIGHT MODAL per visualizzazione */}
            {
                rightModalIsOpen && (
                    <RightModal width="w-4/5">
                        {rightModalMode === 'view' && rightModalJobId && (
                            <ViewBilledJob />
                        )}
                    </RightModal>
                )
            }
        </div>
    );
}
